import { Alert, Box, Skeleton, Stack, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";

import { useWasteClassificationExplanation } from "./api";
import { ClassificationConclusions } from "./ClassificationConclusions";
import { computeDifferences } from "./differences";
import { ExplainableWasteLine } from "./types";
import { WasteClassificationInputs } from "./WasteClassificationInputs";
import { WasteClassificationSections } from "./WasteClassificationSections";

export interface WasteClassificationDialogProps {
  open: boolean;
  line: ExplainableWasteLine;
  onClose: () => void;
  /** Host context for the title, e.g. "Line Item #3". */
  subtitle?: string;
}

/**
 * Explains how the compliance engine reached this line's verdict. Every
 * verdict is what the rules produce from the line's codes, description and
 * jurisdiction; where the line carries something else, the verdict says so.
 */
export function WasteClassificationDialog({
  open,
  line,
  onClose,
  subtitle,
}: WasteClassificationDialogProps) {
  const { explanation, loading, error } = useWasteClassificationExplanation(line.id);
  const differences = explanation ? computeDifferences(explanation.result, line) : {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      title={
        <Stack>
          <Typography variant="h6" fontWeight={700}>
            How this waste was classified
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
      }
      noPrimaryAction
      hideCancelButton
      // With no action bar, the last card is the dialog's bottom edge. The
      // padding goes on the content rather than on DialogContent, which is a
      // scrolling flex container — browsers drop its bottom padding at the
      // end of the scroll.
      contentSx={{ pb: 4 }}
    >
      {/*
        cache-and-network reports loading:true even when a cached explanation
        is already in hand (e.g. reopening the dialog after an edit), so the
        skeleton is gated on "no explanation yet" too — otherwise every
        reopen would flash the skeleton over the still-valid cached answer.
      */}
      {loading && !explanation && <Skeleton variant="rectangular" height={320} />}
      {!loading && error && (
        <Alert severity="error">Could not load the classification explanation.</Alert>
      )}
      {!loading && !error && !explanation && (
        <Typography variant="body2">
          No classification explanation is available for this waste line.
        </Typography>
      )}
      {!error && explanation && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            <WasteClassificationInputs explanation={explanation} />
            <ClassificationConclusions explanation={explanation} differences={differences} />
          </Box>
          <WasteClassificationSections explanation={explanation} differences={differences} />
        </Box>
      )}
    </Dialog>
  );
}
